import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  IconButton,
  Tooltip,
  useMediaQuery,
  Alert
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Add as AddIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  Delete as DeleteIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  Close
} from '@mui/icons-material';
import CollectionCoverGrid from '../components/organisms/CollectionCoverGrid';
import CollectionModal from '../components/organisms/Dialogs/CollectionModal';
import ConfirmDialog from '../components/molecules/ConfirmDialog';
import PageToolbar from '../components/organisms/PageToolbar';
import ModernToast from '../components/molecules/ModernToast';
import api from '../services/api';

// Opções de ordenação para Coleções
const COLLECTION_SORT_OPTIONS = [
  { value: 'name_asc', label: 'Nome (A-Z)' },
  { value: 'name_desc', label: 'Nome (Z-A)' },
  { value: 'articles_desc', label: 'Mais Artigos' },
  { value: 'articles_asc', label: 'Menos Artigos' },
  { value: 'date_desc', label: 'Mais Recentes' },
  { value: 'date_asc', label: 'Mais Antigas' }
];

const CollectionsPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Estados da toolbar
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState(() => localStorage.getItem('collections_sortBy') || 'name_asc');
  const [variant, setVariant] = useState(() => localStorage.getItem('collections_variant') || 'grid');

  // Estados de seleção
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [collectionToDelete, setCollectionToDelete] = useState(null);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);

  // No mobile, forçar visualização grid (list não funciona bem)
  useEffect(() => {
    if (isMobile && variant === 'list') {
      setVariant('grid');
    }
  }, [isMobile, variant]);

  useEffect(() => {
    loadCollections();
  }, []);

  const loadCollections = async () => {
    try {
      setLoading(true);
      const response = await api.get('/collections');
      setCollections(response.data.data);
      setError(null);
    } catch (err) {
      setError('Erro ao carregar coleções');
      console.error('Erro ao carregar coleções:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar e ordenar coleções
  const filteredCollections = useMemo(() => {
    let result = [...collections];

    // Filtrar por busca
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(collection =>
        collection.name.toLowerCase().includes(query) ||
        collection.description?.toLowerCase().includes(query)
      );
    }

    // Ordenar
    result.sort((a, b) => {
      switch (sortBy) {
        case 'name_asc':
          return a.name.localeCompare(b.name);
        case 'name_desc':
          return b.name.localeCompare(a.name);
        case 'articles_desc':
          return (b.articleCount || 0) - (a.articleCount || 0);
        case 'articles_asc':
          return (a.articleCount || 0) - (b.articleCount || 0);
        case 'date_desc':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'date_asc':
          return new Date(a.createdAt) - new Date(b.createdAt);
        default:
          return 0;
      }
    });

    return result;
  }, [collections, searchQuery, sortBy]);

  // Handlers da toolbar
  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleSortChange = (value) => {
    setSortBy(value);
    localStorage.setItem('collections_sortBy', value);
  };

  const handleVariantChange = (_, newVariant) => {
    if (newVariant) {
      setVariant(newVariant);
      localStorage.setItem('collections_variant', newVariant);
    }
  };

  // Handlers de seleção
  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    if (selectionMode) {
      setSelectedCollections([]);
    }
  };

  const handleCollectionSelect = (collectionId) => {
    setSelectedCollections(prev => {
      if (prev.includes(collectionId)) {
        return prev.filter(id => id !== collectionId);
      }
      return [...prev, collectionId];
    });
  };

  const handleSelectAll = () => {
    if (selectedCollections.length === filteredCollections.length) {
      setSelectedCollections([]);
    } else {
      setSelectedCollections(filteredCollections.map(c => c._id));
    }
  };

  // Handlers de coleção
  const handleCollectionClick = (collection) => {
    if (selectionMode) {
      handleCollectionSelect(collection._id);
    } else {
      // Navegar para página de artigos da coleção
      navigate(`/colecoes/${collection._id}/artigos`);
    }
  };

  const handleCreateClick = () => {
    setSelectedCollection(null);
    setModalOpen(true);
  };

  const handleEditCollection = (collection) => {
    setSelectedCollection(collection);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedCollection(null);
  };

  const handleDeleteClick = (collection) => {
    if (!collection || !collection._id) {
      console.error('Tentativa de remover coleção inválida:', collection);
      setSnackbar({ open: true, message: 'Coleção inválida para remoção.', severity: 'error' });
      return;
    }
    setCollectionToDelete(collection);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!collectionToDelete || !collectionToDelete._id) return;
    try {
      await api.delete(`/collections/${collectionToDelete._id}`);
      setCollections(prev => prev.filter(c => c._id !== collectionToDelete._id));
      setSnackbar({ open: true, message: 'Coleção excluída com sucesso!', severity: 'success' });
    } catch (err) {
      console.error('Erro ao excluir coleção:', err);
      setSnackbar({ open: true, message: err.message || 'Erro ao excluir coleção', severity: 'error' });
    } finally {
      setConfirmDialogOpen(false);
      setCollectionToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDialogOpen(false);
    setCollectionToDelete(null);
  };

  const handleBulkDelete = () => {
    if (selectedCollections.length === 0) return;
    setBulkDeleteDialogOpen(true);
  };

  const handleConfirmBulkDelete = async () => {
    try {
      await Promise.all(selectedCollections.map(id => api.delete(`/collections/${id}`)));
      setCollections(prev => prev.filter(c => !selectedCollections.includes(c._id)));
      setSnackbar({
        open: true,
        message: `${selectedCollections.length} coleção(ões) excluída(s) com sucesso!`,
        severity: 'success'
      });
      setSelectedCollections([]);
      setSelectionMode(false);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || 'Erro ao excluir coleções',
        severity: 'error'
      });
    } finally {
      setBulkDeleteDialogOpen(false);
    }
  };

  const handleCreateCollection = async (newCollection) => {
    try {
      const res = await api.post('/collections', newCollection);
      setCollections(prev => [...prev, res.data.data]);
      setSnackbar({ open: true, message: 'Coleção criada com sucesso!', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Erro ao criar coleção', severity: 'error' });
      throw err;
    }
  };

  const handleSaveCollection = async (updatedCollection) => {
    try {
      const res = await api.put(`/collections/${updatedCollection._id}`, updatedCollection);
      setCollections(prev =>
        prev.map(c => c._id === updatedCollection._id ? res.data.data : c)
      );
      setSnackbar({ open: true, message: 'Coleção atualizada com sucesso!', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Erro ao atualizar coleção', severity: 'error' });
      throw err;
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Toolbar */}
      <PageToolbar
        title="Coleções"
        searchValue={searchQuery}
        onSearchChange={handleSearch}
        searchPlaceholder="Buscar coleções..."
        sortValue={sortBy}
        onSortChange={handleSortChange}
        sortOptions={COLLECTION_SORT_OPTIONS}
        viewValue={variant}
        onViewChange={handleVariantChange}
        viewOptions={[
          { value: 'grid', icon: ViewModuleIcon, tooltip: 'Visualização em grade' },
          { value: 'list', icon: ViewListIcon, tooltip: 'Visualização em lista' }
        ]}
        resultCount={filteredCollections.length}
        resultLabel="coleção(ões) encontrada(s)"
        fixed={true}
        onNewItem={handleCreateClick}
        newItemLabel="Nova Coleção"
        extraControls={
          <>
            {selectionMode && selectedCollections.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleBulkDelete}
                sx={{
                  height: 36,
                  px: 1.5,
                  py: 0.5,
                  fontSize: '0.8125rem',
                  borderRadius: 1
                }}
              >
                Excluir ({selectedCollections.length})
              </Button>
            )}
            <Button
              variant={selectionMode ? "contained" : "outlined"}
              color={selectionMode ? "secondary" : "primary"}
              onClick={toggleSelectionMode}
              startIcon={selectionMode ? <Close /> : <CheckBoxIcon />}
              sx={{
                height: 36,
                px: 1.5,
                py: 0.5,
                fontSize: '0.8125rem',
                borderRadius: 1
              }}
            >
              {selectionMode ? 'Cancelar' : 'Selecionar'}
            </Button>
            {selectionMode && (
              <Tooltip title={selectedCollections.length === filteredCollections.length ? "Desmarcar todos" : "Selecionar todos"}>
                <IconButton
                  onClick={handleSelectAll}
                  color="primary"
                  sx={{ width: 36, height: 36, borderRadius: 1 }}
                >
                  {selectedCollections.length === filteredCollections.length ? (
                    <CheckBoxIcon />
                  ) : (
                    <CheckBoxOutlineBlankIcon />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </>
        }
        headerActions={
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateClick}
            sx={{
              height: 36,
              px: 1.5,
              py: 0.5,
              fontSize: '0.8125rem',
              borderRadius: 1
            }}
          >
            Nova Coleção
          </Button>
        }
      />

      {/* Content */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : filteredCollections.length === 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Nenhuma coleção encontrada
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchQuery ? 'Tente ajustar sua busca' : 'Crie sua primeira coleção clicando em "Nova Coleção"'}
          </Typography>
        </Box>
      ) : (
        <CollectionCoverGrid
          collections={filteredCollections}
          variant={variant}
          onCollectionClick={handleCollectionClick}
          onEditCollection={handleEditCollection}
          onDeleteCollection={handleDeleteClick}
          selectionMode={selectionMode}
          selectedCollections={selectedCollections}
          onCollectionSelect={handleCollectionSelect}
        />
      )}

      {/* Modal de criação/edição */}
      <CollectionModal
        open={modalOpen}
        onClose={handleCloseModal}
        collection={selectedCollection}
        onCreateCollection={handleCreateCollection}
        onSaveCollection={handleSaveCollection}
      />

      {/* Dialog de confirmação para exclusão individual */}
      <ConfirmDialog
        open={confirmDialogOpen}
        title="Excluir Coleção"
        message={`Tem certeza que deseja excluir a coleção "${collectionToDelete?.name}"?`}
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      {/* Dialog de confirmação para exclusão em massa */}
      <ConfirmDialog
        open={bulkDeleteDialogOpen}
        title="Excluir Coleções Selecionadas"
        message={`Tem certeza que deseja excluir ${selectedCollections.length} coleção(ões) selecionada(s)?`}
        confirmText="Excluir Todas"
        cancelText="Cancelar"
        onConfirm={handleConfirmBulkDelete}
        onCancel={() => setBulkDeleteDialogOpen(false)}
      />

      {/* ModernToast */}
      <ModernToast
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleCloseSnackbar}
        duration={2500}
        position="bottom-center"
      />
    </Box>
  );
};

export default CollectionsPage;
